/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

// NcrReviewModal.jsx
import { useEffect, useState, useRef } from "react";
import RichTextEditor from "../components/editor/RichTextEditor";
import { getIncidentNcrDraft } from "../api/incidents";
import { ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { getLatestDocument, 
         saveDocumentDraft, 
         commitDocument, 
         getDocumentHistory, 
         finalizeDocument 
        } from "../api/documents";

import { getWorkItem } from "../utils/workItems";
import { getAssigneeOptions } from "../utils/assigneeOptions";

function normalizeSections(input = {}) {
  return {
    incident_summary: input.incident_summary || "",
    what_happened: input.what_happened || "",
    key_facts: input.key_facts || "",
    decision_taken: input.decision_taken || "",
    next_steps: input.next_steps || "",
    risk_signals: input.risk_signals || "",
    open_questions: input.open_questions || "",
    transcript: input.transcript || "",
  };
}

function normalizeProposedWorkItems(items = []) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .filter((item) => item && item.candidate_id && item.title)
    .map((item) => ({
      candidate_id: item.candidate_id,
      title: item.title || "",
      description: item.description || "",
      priority: item.priority || "medium",
      source_section: item.source_section || "next_steps",
      source_text: item.source_text || "",
      included: item.included !== false,
      due_date: item.due_date || null,
      assigned_to_aad_object_id:
        item.assigned_to_aad_object_id || null,
      assigned_to_type: item.assigned_to_type || null,
      assigned_to_name: item.assigned_to_name || "",
      assigned_to_email: item.assigned_to_email || "",
      assigned_to_department:
        item.assigned_to_department || "",
    }));
}

function parseNcrDraft(html = "") {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  console.log(
    "Found headings:",
    Array.from(doc.querySelectorAll("h2, h3")).map((h) => h.textContent)
  );

  function sectionAfterHeading(label) {
    const headings = Array.from(doc.querySelectorAll("h2, h3"));
    const heading = headings.find((h) =>
      h.textContent?.trim().toLowerCase().startsWith(label.toLowerCase())
    );

    if (!heading) return "";

    const nodes = [];
    let node = heading.nextElementSibling;

    while (node && !["H2", "H3"].includes(node.tagName)) {
      if (node.tagName !== "HR") {
        nodes.push(node.outerHTML);
      }
      node = node.nextElementSibling;
    }

    return nodes.join("");
  }

  function firstHeadingText(label) {
    const headings = Array.from(doc.querySelectorAll("h2, h3"));
    const heading = headings.find((h) =>
      h.textContent?.trim().toLowerCase().startsWith(label.toLowerCase())
    );

    return heading ? `<p>${heading.textContent.trim()}</p>` : "";
  }
  console.log("DOM parsed sections:", {
    incident_summary: firstHeadingText("Incident Summary"),
    what_happened: sectionAfterHeading("What Happened"),
    key_facts: sectionAfterHeading("Key Facts"),
  });
  return {
    incident_summary: firstHeadingText("Incident Summary"),
    what_happened: sectionAfterHeading("What Happened"),
    key_facts: sectionAfterHeading("Key Facts"),
    decision_taken: sectionAfterHeading("Decision Taken"),
    next_steps: sectionAfterHeading("Next Steps"),
    risk_signals: sectionAfterHeading("Risk Signals"),
    open_questions: sectionAfterHeading("Open Questions"),
    transcript: sectionAfterHeading("Transcript"),
  };
}

function AttestationDetails({ contentHash, previousHash }) {
  return (
    <div className="mt-3 space-y-3 rounded-xl bg-slate-50 p-3 text-xs dark:bg-slate-900">
      <div>
        <p className="font-semibold text-slate-700 dark:text-slate-300">
          SHA-256
        </p>

        <p className="mt-1 break-all font-mono text-slate-500">
          {contentHash || "Unavailable"}
        </p>
      </div>

      {previousHash && (
        <div>
          <p className="font-semibold text-slate-700 dark:text-slate-300">
            Previous hash
          </p>

          <p className="mt-1 break-all font-mono text-slate-500">
            {previousHash}
          </p>
        </div>
      )}
    </div>
  );
}

function formatEnumLabel(value) {
  if (!value) return "";

  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function PriorityBadge({ priority }) {
  const normalizedPriority = priority?.toLowerCase();

  const priorityClass =
    normalizedPriority === "urgent"
      ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
      : normalizedPriority === "high"
        ? "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300"
        : normalizedPriority === "medium"
          ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300"
          : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";

  return (
    <span
      className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium capitalize ${priorityClass}`}
    >
      {priority || "No priority"}
    </span>
  );
}

function CreatedWorkItemsList({
  workItems = [],
  onWorkItemClick,
}) {
  if (workItems.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950">
        No work items were created.
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          Created work items
        </p>

        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {workItems.length}
        </span>
      </div>

      <div className="mt-3 space-y-2">
        {workItems.map((workItem) => (
          <button
            key={workItem.id}
            type="button"
            onClick={() => onWorkItemClick?.(workItem)}
            className="block w-full rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-blue-300 hover:bg-blue-50/40 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-blue-800 dark:hover:bg-blue-950/20"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  {workItem.title}
                </p>

                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span>
                    Owner:{" "}
                    {workItem.assigned_to_name ||
                      workItem.assigned_to_email ||
                      "Unassigned"}
                  </span>

                  {workItem.due_date && (
                    <span>
                      Due:{" "}
                      {new Date(
                        workItem.due_date
                      ).toLocaleDateString()}
                    </span>
                  )}

                  <span>
                    Status: {formatEnumLabel(workItem.status)}
                  </span>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <PriorityBadge priority={workItem.priority} />

                <span
                  aria-hidden="true"
                  className="text-lg text-slate-400"
                >
                  ›
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function WorkItemDetailsDrawer({
  workItem,
  incidentNumber,
  loading = false,
  error = "",
  onClose,
}) {
  const drawerRef = useRef(null);

  const isOpen = Boolean(workItem || loading || error);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const owner =
    workItem?.assigned_to_name ||
    workItem?.assigned_to_email ||
    "Unassigned";

  return (
    <div
      className="absolute inset-0 z-40 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="work-item-drawer-title"
    >
      <button
        type="button"
        aria-label="Close work item details"
        onClick={onClose}
        className="absolute inset-0 bg-black/30"
      />

      <aside
        ref={drawerRef}
        className="relative z-10 flex h-full w-full max-w-lg flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
              Corrective Work Item
            </p>

            <h2
              id="work-item-drawer-title"
              className="mt-1 text-xl font-semibold text-slate-900 dark:text-white"
            >
              {loading
                ? "Loading work item"
                : error
                  ? "Work item unavailable"
                  : workItem?.title || "Untitled work item"}
            </h2>

            {!loading && !error && workItem && incidentNumber && (
              <p className="mt-1 text-sm text-slate-500">
                Created from NCR {incidentNumber}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-slate-800 dark:hover:text-white"
            aria-label="Close work item details"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex min-h-48 items-center justify-center">
              <p className="text-sm text-slate-500">
                Loading work item...
              </p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
              <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                Unable to open work item
              </p>

              <p className="mt-1 text-sm leading-6 text-red-700 dark:text-red-300">
                {error}
              </p>
            </div>
          ) : workItem ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <PriorityBadge priority={workItem.priority} />

                <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {formatEnumLabel(workItem.status) || "Open"}
                </span>
              </div>

              <dl className="mt-6 divide-y divide-slate-200 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
                <div className="grid grid-cols-[120px_1fr] gap-4 px-4 py-3">
                  <dt className="text-sm text-slate-500">Owner</dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">
                    {owner}
                  </dd>
                </div>

                <div className="grid grid-cols-[120px_1fr] gap-4 px-4 py-3">
                  <dt className="text-sm text-slate-500">
                    Due date
                  </dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">
                    {workItem.due_date
                      ? new Date(
                          workItem.due_date
                        ).toLocaleDateString()
                      : "No due date"}
                  </dd>
                </div>

                <div className="grid grid-cols-[120px_1fr] gap-4 px-4 py-3">
                  <dt className="text-sm text-slate-500">Status</dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">
                    {formatEnumLabel(workItem.status) || "Open"}
                  </dd>
                </div>

                <div className="grid grid-cols-[120px_1fr] gap-4 px-4 py-3">
                  <dt className="text-sm text-slate-500">
                    Priority
                  </dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white">
                    {formatEnumLabel(workItem.priority) ||
                      "No priority"}
                  </dd>
                </div>
              </dl>

              <section className="mt-6">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Description
                </h3>

                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {workItem.description ||
                    "No additional description was provided."}
                </p>
              </section>

              <section className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                  Operational continuity
                </p>

                <p className="mt-1 text-sm leading-6 text-blue-800 dark:text-blue-300">
                  This action was created when the NCR was finalized
                  and remains linked to the original incident record.
                </p>
              </section>
            </>
          ) : null}
        </div>

        <div className="border-t border-slate-200 px-6 py-4 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
          >
            Close
          </button>
        </div>
      </aside>
    </div>
  );
}


function CommitHistoryPanel({ timelineItems = [], onWorkItemClick, }) {
  const [expandedCommitKey, setExpandedCommitKey] = useState(null);

  const toggleAttestation = (key) => {
    setExpandedCommitKey((currentKey) =>
      currentKey === key ? null : key
    );
  };

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
      <h3 className="text-lg font-semibold">Document Timeline</h3>

      <p className="mt-1 text-sm text-slate-500">
        A record of how this NCR moved from captured evidence to finalized
        corrective work.
      </p>

      {timelineItems.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">
          No timeline activity yet.
        </p>
      ) : (
        <div className="relative mt-6">
          <div className="absolute bottom-3 left-[11px] top-3 w-[2px] bg-slate-200 dark:bg-slate-800" />

          <div className="space-y-5">
            {timelineItems.map((item) => {
              const expanded = expandedCommitKey === item.key;
              const isDraft = item.type === "ai_draft";
              const isFinalized = item.type === "finalized";
              const isCommit = item.type === "commit";

              const nodeClass = isFinalized
                ? "bg-blue-600"
                : isCommit
                  ? "bg-green-600"
                  : "bg-violet-500";

              const badgeClass = isFinalized
                ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                : isCommit
                  ? "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300"
                  : "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300";

              return (
                <div
                  key={item.key}
                  className="relative flex items-start gap-4"
                >
                  <div
                    className={`relative z-10 mt-2 h-6 w-6 rounded-full border-4 border-white dark:border-slate-950 ${nodeClass}`}
                  />

                  <div
                    className={`flex-1 rounded-xl border p-4 shadow-sm ${
                      isFinalized
                        ? "border-blue-200 bg-blue-50/40 dark:border-blue-900 dark:bg-blue-950/20"
                        : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">
                          {item.title}
                        </p>

                        <p className="mt-0.5 text-xs text-slate-500">
                          {item.subtitle}
                        </p>
                      </div>

                      <span
                        className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium ${badgeClass}`}
                      >
                        {item.badge}
                      </span>
                    </div>

                    <p className="mt-3 text-sm text-slate-700 dark:text-slate-300">
                      {item.summary}
                    </p>

                    <div className="mt-3 space-y-1 text-xs text-slate-500">
                      <p>{item.actor}</p>

                      {item.occurredAt && (
                        <p>
                          {new Date(item.occurredAt).toLocaleString()}
                        </p>
                      )}
                    </div>

                    {isFinalized && (
                      <CreatedWorkItemsList
                        workItems={item.createdWorkItems}
                        onWorkItemClick={onWorkItemClick}
                      />
                    )}

                    {!isDraft && (
                      <div className="mt-4 flex flex-wrap items-center gap-4">
                        <button
                          type="button"
                          onClick={() => toggleAttestation(item.key)}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {expanded
                            ? "Hide attestation"
                            : "View attestation"}
                        </button>

                        <button
                          type="button"
                          disabled
                          title="Revision comparison will be added next"
                          className="cursor-not-allowed text-xs font-semibold text-slate-400"
                        >
                          View changes
                        </button>
                      </div>
                    )}

                    {expanded && (
                      <AttestationDetails
                        contentHash={item.contentHash}
                        previousHash={item.previousHash}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function formatActor(item, fallback = "Unknown user") {
  if (item.actor_name && item.actor_email) {
    return `${item.actor_name} (${item.actor_email})`;
  }

  return item.actor_name || item.actor_email || fallback;
}

function buildDocumentTimeline(historyItems = []) {
  const sortedItems = [...historyItems].sort(
    (a, b) => new Date(a.occurred_at) - new Date(b.occurred_at)
  );

  const initialRevision = sortedItems.find(
    (item) =>
      item.item_type === "revision" &&
      item.revision_number === 1
  );

  const commits = sortedItems.filter(
    (item) => item.item_type === "commit"
  );

  const timeline = [];

  if (initialRevision) {
    timeline.push({
      type: "ai_draft",
      key: `ai-draft-${initialRevision.document_id}`,
      title: "Initial AI Draft",
      subtitle: "Generated from captured operational evidence",
      badge: "AI Generated",
      summary:
        initialRevision.change_summary ||
        "Fluxonix created the initial structured NCR draft from the captured evidence.",
      actor: "Fluxonix AI",
      occurredAt: initialRevision.occurred_at,
      revisionNumber: initialRevision.revision_number,
      contentHash: initialRevision.content_hash,
      previousHash: initialRevision.previous_hash,
      createdWorkItems: [],
    });
  }

  commits.forEach((commit) => {
    const isFinal = Boolean(commit.is_final);

    timeline.push({
      type: isFinal ? "finalized" : "commit",
      key: `commit-${commit.commit_number}-${commit.occurred_at}`,
      title: isFinal
        ? "NCR Finalized"
        : `Review Commit ${commit.commit_number}`,
      subtitle: isFinal
        ? `Final revision ${commit.revision_number}`
        : `Revision ${commit.revision_number} committed`,
      badge: isFinal ? "Finalized" : "Verified",
      summary:
        commit.change_summary ||
        (isFinal
          ? "The NCR was finalized and follow-up work was created."
          : "Reviewed changes were committed."),
      actor: formatActor(commit),
      occurredAt: commit.occurred_at,
      revisionNumber: commit.revision_number,
      commitNumber: commit.commit_number,
      contentHash: commit.content_hash,
      previousHash: commit.previous_hash,
      createdWorkItems: commit.created_work_items || [],
    });
  });

  return timeline;
}

export default function NcrReviewModal({ incidentId, incidentNumber, onClose }) {
  const [sections, setSections] = useState({
    incident_summary: "",
    what_happened: "",
    key_facts: "",
    decision_taken: "",
    next_steps: "",
    risk_signals: "",
    open_questions: "",
    transcript: "",
  });

  const [reviewStatus, setReviewStatus] = useState({
    incident_summary: false,
    what_happened: false,
    key_facts: false,
    decision_taken: false,
    next_steps: false,
    risk_signals: false,
    open_questions: false,
  });

  const [showCommitConfirm, setShowCommitConfirm] = useState(false);
  const [commitSummary, setCommitSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorVersion, setEditorVersion] = useState(0);
  const [documentId, setDocumentId] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [documentMetadata, setDocumentMetadata] = useState({
    revisionNumber: 0,
    commitNumber: 0,
    status: "AI Draft",
  });
  const [documentTimeline, setDocumentTimeline] = useState([]);
  const [isFinal, setIsFinal] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [finalSummary, setFinalSummary] = useState("");
  const [proposedWorkItems, setProposedWorkItems] = useState([]);

  const { user } = useAuth();
  const sectionRefs = useRef({});

  const reviewItems = [
    { key: "incident_summary", label: "Incident Summary", editable: true },
    { key: "what_happened", label: "What Happened", editable: true },
    { key: "key_facts", label: "Key Facts", editable: true },
    { key: "decision_taken", label: "Decision Taken", editable: true },
    { key: "next_steps", label: "Next Steps", editable: true },
    { key: "risk_signals", label: "Risk Signals", editable: true },
    { key: "open_questions", label: "Open Questions", editable: true },
    { key: "transcript", label: "Transcript", editable: false },
  ];
  
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [selectedWorkItem, setSelectedWorkItem] = useState(null);
  const [workItemLoading, setWorkItemLoading] = useState(false);
  const [workItemError, setWorkItemError] = useState("");
  
  const reviewableItems = reviewItems.filter((item) => item.editable);

  const includedWorkItemCount = proposedWorkItems.filter(
    (item) => item.included
  ).length;

  const includedWorkItemsWithoutAssignee =
    proposedWorkItems.filter(
      (item) =>
        item.included &&
        !item.assigned_to_aad_object_id
  );

  const allIncludedWorkItemsAssigned =
  includedWorkItemsWithoutAssignee.length === 0;

  const completedCount = reviewableItems.filter(
    (item) => reviewStatus[item.key]
  ).length;

  async function handleWorkItemClick(workItem) {
    if (!workItem?.id) {
      setWorkItemError("This work item does not have a valid ID.");
      setSelectedWorkItem(null);
      return;
    }

    setSelectedWorkItem(null);
    setWorkItemError("");
    setWorkItemLoading(true);

    try {
      const currentWorkItem = await getWorkItem(workItem.id);
      setSelectedWorkItem(currentWorkItem);
    } catch (err) {
      console.error("Failed to load work item:", err);

      const status = err.response?.status;

      if (status === 404) {
        setWorkItemError(
          "This work item could not be found or is no longer accessible."
        );
      } else {
        setWorkItemError(
          err.response?.data?.detail ||
            "Unable to load the work item. Please try again."
        );
      }
    } finally {
      setWorkItemLoading(false);
    }
  }


  function updateProposedWorkItem(candidateId, changes) {
    setDirty(true);

    setProposedWorkItems((previous) =>
        previous.map((item) =>
        item.candidate_id === candidateId
            ? {
                ...item,
                ...changes,
            }
            : item
        )
    );
  }

  function toggleProposedWorkItem(candidateId) {
    setDirty(true);

    setProposedWorkItems((previous) =>
        previous.map((item) =>
        item.candidate_id === candidateId
            ? {
                ...item,
                included: !item.included,
            }
            : item
        )
    );
  }

  useEffect(() => {
    let cancelled = false;

    async function loadAssignees() {
      setAssigneesLoading(true);

      try {
        const data = await getAssigneeOptions();

        if (!cancelled) {
          setAssigneeOptions(data.items || []);
        }
      } catch (err) {
        console.error(
          "Failed to load assignee options:",
          err
        );

        if (!cancelled) {
          setAssigneeOptions([]);
        }
      } finally {
        if (!cancelled) {
          setAssigneesLoading(false);
        }
      }
    }

    loadAssignees();

    return () => {
      cancelled = true;
    };
  }, []);


  async function loadDocumentHistory(id) {
    if (!id) return;

    try {
      const history = await getDocumentHistory(id);

      const timeline = buildDocumentTimeline(history.items || []);

      setDocumentTimeline(timeline);
    } catch (err) {
      console.error("Failed to load document history:", err);
      setDocumentTimeline([]);
    }
  }

  function updateSection(key, value) {
    if (isFinal) return;
    setDirty(true);

    setSections((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function markSectionReviewed(key) {
    if (isFinal) return;
    setReviewStatus((prev) => ({
      ...prev,
      [key]: true,
    }));
  }

  function unmarkSectionReviewed(key) {
    if (isFinal) return;
    setReviewStatus((prev) => ({
      ...prev,
      [key]: false,
    }));
  }

  useEffect(() => {
    async function loadDraft() {
      if (!incidentId) return;
      setSelectedWorkItem(null);
      setLoading(true);
      setError("");

      try {
        const data = await getLatestDocument("ncr", incidentId);
        
        console.log("Latest document response:", data);
        console.log("sections from response:", data.sections);
        console.log("review_status from response:", data.review_status);
        setDocumentId(data.document_id);
        setIsFinal(data.is_final || data.document_status === "final");

        await loadDocumentHistory(data.document_id);
        
        const normalizedSections = normalizeSections(data.sections || {});
        console.log("normalized sections:", normalizedSections);
        const normalizedWorkItems = normalizeProposedWorkItems(
        data.proposed_work_items ||
            data.metadata?.proposed_work_items ||
            []
        );
        setProposedWorkItems(normalizedWorkItems);

        setSections(normalizedSections);
        setReviewStatus(data.review_status || {});
        setDirty(false);
        setEditorVersion((v) => v + 1);
        } catch (err) {
        if (err?.response?.status !== 404) {
            throw err;
        }

        console.log("No structured document found. Bootstrapping from legacy NCR HTML.");
        const legacyData = await getIncidentNcrDraft(incidentId);
        console.log("Legacy NCR draft raw:", legacyData.ncr_draft);
        const extractedWorkItems =
          normalizeProposedWorkItems(
            legacyData.structured_json?.proposed_work_items ||
            []
          );

        setProposedWorkItems(extractedWorkItems);
        
        const parsedSections = parseNcrDraft(legacyData.ncr_draft || "");
        setSections(parsedSections);
        const initialReviewStatus = {
            incident_summary: false,
            what_happened: false,
            key_facts: false,
            decision_taken: false,
            next_steps: false,
            risk_signals: false,
            open_questions: false,
        };

        setReviewStatus(initialReviewStatus);
        const saved = await saveDocumentDraft({
            document_type: "ncr",
            reference_id: incidentId,
            sections: parsedSections,
            proposed_work_items: extractedWorkItems,
            review_status: initialReviewStatus,
            metadata: {
            ui_version: 1,
            bootstrapped_from: "legacy_ncr_html",
            },
        });
        setDocumentId(saved.document_id);
        await loadDocumentHistory(saved.document_id);
        setDirty(false);
        setDocumentMetadata(prev => ({
          ...prev,
          revisionNumber: saved.revision_number,
        }));
        setEditorVersion((v) => v + 1);
        } finally {
          setLoading(false);
        }
    }

    loadDraft();
  }, [incidentId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50">
      <div className="relative h-full w-full md:w-[90vw] md:h-[90vh] md:mx-auto md:my-8 bg-white dark:bg-slate-950 rounded-2xl shadow-xl overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Review NCR</h2>
            <p className="text-sm text-slate-500">{incidentNumber}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 space-y-4">
            <h3 className="font-semibold">NCR Draft</h3>

            {loading ? (
              <p className="text-sm text-slate-500">Loading NCR draft...</p>
            ) : error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : (
              <div className="space-y-5">
                {reviewItems.map((item) => {

                    return (
                    <div
                        key={item.key}
                        id={item.key}
                        ref={(el) => {
                        sectionRefs.current[item.key] = el;
                        }}
                    >
                        <NcrSectionEditor
                        key={`${item.key}-${editorVersion}`}
                        title={item.label}
                        value={sections[item.key] || ""}
                        onChange={(value) => updateSection(item.key, value)}
                        editable={item.editable}
                        disabled={isFinal}
                        reviewed={reviewStatus[item.key]}
                        onMarkReviewed={() => markSectionReviewed(item.key)}
                        onUnmarkReviewed={() => unmarkSectionReviewed(item.key)}
                        />
                    </div>
                    );
                })}
                <ProposedWorkItemsEditor
                  items={proposedWorkItems}
                  assigneeOptions={assigneeOptions}
                  assigneesLoading={assigneesLoading}
                  disabled={isFinal}
                  onUpdate={updateProposedWorkItem}
                  onToggle={toggleProposedWorkItem}
                />
              </div>
            )}
          </section>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
              <h3 className="font-semibold text-lg">Document Metadata</h3>

              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Status</span>
                    {dirty ? (
                      <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-800">
                      Unsaved Changes
                      </span>
                    ) : (
                    <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
                      AI Draft
                    </span>
                  )}
                </div>

                <div className="flex justify-between">
                  <span>Version</span>
                  <span>v0</span>
                </div>

                <div className="flex justify-between">
                  <span>Current Reviewer</span>
                  <span>{user?.full_name || user?.email || "Unknown user"}</span>
                </div>

                <div className="flex justify-between">
                  <span>Last Edited</span>
                  <span>2 mins ago</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
              <h3 className="font-semibold text-lg">Review Progress</h3>

              <div className="mt-4 space-y-2">
                {reviewableItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() =>
                      sectionRefs.current[item.key]?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      })
                    }
                    className="w-full flex justify-between text-sm hover:text-blue-600 text-left"
                  >
                    <span>{item.label}</span>

                    {reviewStatus[item.key] ? (
                      <span className="text-green-600">✓</span>
                    ) : (
                      <span className="text-slate-400">○</span>
                    )}
                  </button>
                ))}
              </div>

              <div className="mt-5">
                <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                  <div
                    className="h-full bg-blue-600"
                    style={{
                      width: `${(completedCount / reviewableItems.length) * 100}%`,
                    }}
                  />
                </div>

                <p className="mt-2 text-xs text-slate-500">
                  {completedCount} of {reviewableItems.length} sections reviewed
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm">
            <h3 className="font-semibold text-lg">Actions</h3>

            <div className="mt-4 space-y-3">
                <button
                type="button"
                disabled={isFinal || !dirty || saving}
                onClick={async () => {
                    if (isFinal) return;
                    setSaving(true);
                    try {
                    await saveDocumentDraft({
                        document_type: "ncr",
                        reference_id: incidentId,
                        sections,
                        proposed_work_items: proposedWorkItems,
                        review_status: reviewStatus,
                        metadata: {
                          ui_version: 1,
                        },
                    });

                    setDirty(false);
                    } finally {
                    setSaving(false);
                    }
                }}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 py-3 disabled:opacity-50"
                >
                {saving ? "Saving..." : "Save Draft"}
                </button>

                <button
                  type="button"
                  disabled={
                    isFinal ||
                    committing ||
                    completedCount === 0
                  }
                  onClick={() => {
                    if (isFinal) return;
                    setShowCommitConfirm(true);
                  }}
                  className="w-full rounded-xl bg-blue-600 text-white py-3 font-semibold disabled:opacity-50"
                >
                  Commit Changes
                </button>

                <button
                  type="button"
                  disabled={
                    isFinal ||
                    finalizing ||
                    completedCount < reviewableItems.length ||
                    !allIncludedWorkItemsAssigned
                  }
                  onClick={() => setShowFinalConfirm(true)}
                  className="w-full rounded-xl bg-emerald-600 text-white py-3 font-semibold disabled:opacity-50"
                >
                  {isFinal ? "Finalized" : "Final Commit"}
                </button>
                {!allIncludedWorkItemsAssigned && !isFinal && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Assign a responsible owner to every selected Work Item
                    before finalizing.
                  </p>
                )}
            </div>
            </div>
            {showCommitConfirm && !isFinal && (
                <div className="rounded-2xl border border-blue-200 dark:border-blue-900 p-5 shadow-sm bg-blue-50 dark:bg-blue-950/30">
                    <h3 className="font-semibold text-lg">Commit Review Changes</h3>

                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    This will create an auditable review checkpoint with your name,
                    timestamp, reviewed sections, and current NCR content.
                    </p>

                    <textarea
                    value={commitSummary}
                    onChange={(e) => setCommitSummary(e.target.value)}
                    placeholder="Briefly summarize what changed..."
                    className="mt-4 w-full min-h-[90px] rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm"
                    />

                    <div className="mt-4 flex gap-2">
                    <button
                        type="button"
                        disabled={committing || !commitSummary.trim()}
                        onClick={async () => {
                            if (isFinal) {
                            setShowCommitConfirm(false);
                            return;
                            }
                            if (!documentId) {
                            console.error("Cannot commit: documentId is missing");
                            return;
                            }

                            setCommitting(true);

                            try {
                            const result = await commitDocument(documentId, {
                                sections,
                                proposed_work_items: proposedWorkItems,
                                review_status: reviewStatus,
                                commit_summary: commitSummary,
                                metadata: {
                                ui_version: 1,
                                document_type: "ncr",
                                reference_id: incidentId,
                                },
                            });

                            console.log("Commit result:", result);
                            await loadDocumentHistory(documentId);                            
                            setDirty(false);
                            setShowCommitConfirm(false);
                            setCommitSummary("");
                            setDocumentMetadata(prev => ({
                                ...prev,
                                revisionNumber: result.revision_number,
                                commitNumber: result.commit_number,
                                status: "Committed",
                            }));
                            } catch (err) {
                            console.error("Failed to commit document:", err);
                            } finally {
                            setCommitting(false);
                            }
                        }}
                        className="flex-1 rounded-xl bg-blue-600 text-white py-2 font-semibold disabled:opacity-50"
                        >
                        {committing ? "Committing..." : "Confirm Commit"}
                    </button>

                    <button
                        type="button"
                        onClick={() => setShowCommitConfirm(false)}
                        className="flex-1 rounded-xl border border-slate-300 dark:border-slate-700 py-2"
                    >
                        Cancel
                    </button>
                    </div>
                </div>
            )}
            {showFinalConfirm && (
                <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900 p-5 shadow-sm bg-emerald-50 dark:bg-emerald-950/30">
                    <h3 className="font-semibold text-lg">Finalize NCR</h3>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                        This will lock the NCR as the official record and create {includedWorkItemCount} selected Work Item
                        {includedWorkItemCount === 1 ? "" : "s"}. Sections will become read-only after finalization.
                    </p>

                    <textarea
                    value={finalSummary}
                    onChange={(e) => setFinalSummary(e.target.value)}
                    placeholder="Briefly summarize why this document is ready to finalize..."
                    className="mt-4 w-full min-h-[90px] rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm"
                    />

                    <div className="mt-4 flex gap-2">
                    <button
                        type="button"
                        disabled={finalizing || !finalSummary.trim()}
                        onClick={async () => {
                        if (!documentId) {
                            console.error("Cannot finalize: documentId is missing");
                            return;
                        }

                        setFinalizing(true);

                        try {
                            const result = await finalizeDocument(documentId, {
                            sections,
                            proposed_work_items: proposedWorkItems,
                            review_status: reviewStatus,
                            final_summary: finalSummary,
                            metadata: {
                                ui_version: 2,
                                document_type: "ncr",
                                reference_id: incidentId,
                            },
                            });

                            console.log("Finalize result:", result);

                            setIsFinal(true);
                            setShowCommitConfirm(false);
                            setCommitSummary("");
                            setDirty(false);
                            setShowFinalConfirm(false);
                            setFinalSummary("");

                            await loadDocumentHistory(documentId);
                        } catch (err) {
                            console.error("Failed to finalize document:", err);
                        } finally {
                            setFinalizing(false);
                        }
                        }}
                        className="flex-1 rounded-xl bg-emerald-600 text-white py-2 font-semibold disabled:opacity-50"
                    >
                        {finalizing ? "Finalizing..."
                            : `Finalize & Create ${includedWorkItemCount} Work Item${
                                includedWorkItemCount === 1 ? "" : "s"
                                }`}
                    </button>

                    <button
                        type="button"
                        disabled={finalizing}
                        onClick={() => setShowFinalConfirm(false)}
                        className="flex-1 rounded-xl border border-slate-300 dark:border-slate-700 py-2 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    </div>
                </div>
                )}
            <CommitHistoryPanel timelineItems={documentTimeline} onWorkItemClick={handleWorkItemClick} />
          </aside>
        </div>
        <WorkItemDetailsDrawer
          workItem={selectedWorkItem}
          incidentNumber={incidentNumber}
          loading={workItemLoading}
          error={workItemError}
          onClose={() => {
            setSelectedWorkItem(null);
            setWorkItemError("");
            setWorkItemLoading(false);
          }}
        />
      </div>
    </div>
  );
}

function NcrSectionEditor({
  title,
  value,
  onChange,
  editable = true,
  reviewed = false,
  onMarkReviewed,
  onUnmarkReviewed,
  disabled = false,
}) {
  const [open, setOpen] = useState(true);
  const [confirmReview, setConfirmReview] = useState(false);

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white"
        >
          {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          {title}
        </button>

        <div className="flex items-center gap-2">
          <span className="text-xs rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 px-2 py-1">
            AI Generated
          </span>

          {editable && !disabled ? (
            <>
              {!reviewed && !confirmReview && (
                <button
                  type="button"
                  onClick={() => setConfirmReview(true)}
                  className="text-xs rounded-full px-2 py-1 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                >
                  Mark Reviewed
                </button>
              )}

              {!reviewed && confirmReview && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      onMarkReviewed();
                      setConfirmReview(false);
                    }}
                    className="text-xs rounded-full px-2 py-1 bg-green-600 text-white"
                  >
                    Confirm Reviewed
                  </button>

                  <button
                    type="button"
                    onClick={() => setConfirmReview(false)}
                    className="text-xs rounded-full px-2 py-1 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {reviewed && (
                <div className="flex items-center gap-1">
                  <span className="text-xs rounded-full px-2 py-1 bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">
                    Reviewed
                  </span>

                  <button
                    type="button"
                    onClick={onUnmarkReviewed}
                    className="text-xs rounded-full px-2 py-1 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                  >
                    Undo
                  </button>
                </div>
              )}

              <button
                type="button"
                className="text-xs flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-600 dark:text-slate-300"
              >
                <Sparkles size={13} />
                AI Assist
              </button>
            </>
          ) : editable && disabled ? (
            <span className="text-xs rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-500">
                Locked
            </span>
        ) : (
            <span className="text-xs rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-500">
                Read only
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="p-4">
          <RichTextEditor value={value} onChange={onChange} editable={editable && !disabled} />
        </div>
      )}
    </div>
  );
}

function ProposedWorkItemsEditor({
  items,
  assigneeOptions = [],
  assigneesLoading = false,
  disabled,
  onUpdate,
  onToggle,
}) {
  const handleAssigneeChange = (candidateId, objectId) => {
    if (!objectId) {
      onUpdate(candidateId, {
        assigned_to_aad_object_id: null,
        assigned_to_type: "user",
        assigned_to_name: null,
        assigned_to_email: null,
        assigned_to_department: null,
      });

      return;
    }

    const selectedAssignee = assigneeOptions.find(
      (assignee) => assignee.id === objectId
    );

    if (!selectedAssignee) {
      return;
    }

    onUpdate(candidateId, {
      assigned_to_aad_object_id: selectedAssignee.id,
      assigned_to_type: selectedAssignee.type,
      assigned_to_name: selectedAssignee.name,
      assigned_to_email: selectedAssignee.email || null,
      assigned_to_department:
        selectedAssignee.department || null,
    });
  };

  const renderAssigneeLabel = (assignee) => {
    const typeLabel =
      assignee.type === "group" ? "Group" : "User";

    if (assignee.email) {
      return `${assignee.name} — ${assignee.email} (${typeLabel})`;
    }

    return `${assignee.name} (${typeLabel})`;
  };

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">
              Proposed Work Items
            </h3>

            <p className="mt-1 text-xs text-slate-500">
              Review the operational commitments that will be
              created when this NCR is finalized.
            </p>
          </div>

          <span className="text-xs rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300 px-2 py-1">
            AI Proposed
          </span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">
            No Work Items were proposed for this NCR.
          </p>
        ) : (
          items.map((item) => (
            <div
              key={item.candidate_id}
              className={`rounded-xl border p-4 ${
                item.included
                  ? "border-blue-200 dark:border-blue-900"
                  : "border-slate-200 dark:border-slate-800 opacity-70"
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={item.included}
                  disabled={disabled}
                  onChange={() =>
                    onToggle(item.candidate_id)
                  }
                  className="mt-1 h-4 w-4"
                />

                <div className="flex-1 space-y-3">
                  <input
                    type="text"
                    value={item.title}
                    disabled={disabled || !item.included}
                    onChange={(event) =>
                      onUpdate(item.candidate_id, {
                        title: event.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 font-semibold disabled:opacity-60"
                  />

                  <textarea
                    value={item.description || ""}
                    disabled={disabled || !item.included}
                    onChange={(event) =>
                      onUpdate(item.candidate_id, {
                        description: event.target.value,
                      })
                    }
                    rows={3}
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm disabled:opacity-60"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="text-sm">
                      <span className="block mb-1 text-slate-500">
                        Priority
                      </span>

                      <select
                        value={item.priority}
                        disabled={disabled || !item.included}
                        onChange={(event) =>
                          onUpdate(item.candidate_id, {
                            priority: event.target.value,
                          })
                        }
                        className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 disabled:opacity-60"
                      >
                        <option value="low">Low</option>
                        <option value="medium">
                          Medium
                        </option>
                        <option value="high">High</option>
                        <option value="urgent">
                          Urgent
                        </option>
                      </select>
                    </label>

                    <label className="text-sm">
                      <span className="block mb-1 text-slate-500">
                        Due date
                      </span>

                      <input
                        type="date"
                        value={
                          item.due_date
                            ? item.due_date.slice(0, 10)
                            : ""
                        }
                        disabled={disabled || !item.included}
                        onChange={(event) =>
                          onUpdate(item.candidate_id, {
                            due_date:
                              event.target.value || null,
                          })
                        }
                        className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 disabled:opacity-60"
                      />
                    </label>
                  </div>

                  <label className="block text-sm">
                    <span className="block mb-1 text-slate-500">
                      Responsible owner
                    </span>

                    <select
                      value={
                        item.assigned_to_aad_object_id || ""
                      }
                      disabled={
                        disabled ||
                        !item.included ||
                        assigneesLoading
                      }
                      onChange={(event) =>
                        handleAssigneeChange(
                          item.candidate_id,
                          event.target.value
                        )
                      }
                      className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 disabled:opacity-60"
                    >
                      <option value="">
                        {assigneesLoading
                          ? "Loading owners..."
                          : "Select a user or group"}
                      </option>

                      {assigneeOptions.map((assignee) => (
                        <option
                          key={`${assignee.type}-${assignee.id}`}
                          value={assignee.id}
                        >
                          {renderAssigneeLabel(assignee)}
                        </option>
                      ))}
                    </select>

                    {item.assigned_to_aad_object_id &&
                      item.assigned_to_department && (
                        <p className="mt-1 text-xs text-slate-500">
                          Department:{" "}
                          {item.assigned_to_department}
                        </p>
                      )}
                  </label>

                  <div className="rounded-lg bg-slate-50 dark:bg-slate-900 p-3">
                    <p className="text-xs font-semibold text-slate-500">
                      Source:{" "}
                      {item.source_section?.replaceAll(
                        "_",
                        " "
                      ) || "Not specified"}
                    </p>

                    {item.source_text && (
                      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                        {item.source_text}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function NcrTabs({
  activeTab,
  onTabChange,
  workItemCount = 0,
}) {
  const tabs = [
    {
      id: "document",
      label: "Document",
    },
    {
      id: "timeline",
      label: "Timeline",
    },
    {
      id: "work_items",
      label: "Work Items",
      count: workItemCount,
    },
  ];

  return (
    <div className="border-b border-slate-200 dark:border-slate-800">
      <div
        role="tablist"
        aria-label="NCR sections"
        className="flex gap-6"
      >
        {tabs.map((tab) => {
          const selected = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onTabChange(tab.id)}
              className={`border-b-2 px-1 py-3 text-sm font-medium ${
                selected
                  ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                  : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:hover:border-slate-700 dark:hover:text-slate-300"
              }`}
            >
              <span>{tab.label}</span>

              {typeof tab.count === "number" &&
                tab.count > 0 && (
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                      selected
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
            </button>
          );
        })}
      </div>
    </div>
  );
}